import { type Account, type AccountConfig } from '../../../models/Account'
import { type BookingClient, type Location, MeetingMode, type SchedulingEventType } from '../../../models/Scheduling'
import { type User } from '../../../models/User'
import { type LocationInput } from '../../../store/scheduling/dto'
import { clientDisplayName } from '../shared/schedulingDisplay'
import { type EmailTemplateContext } from './renderEmailTemplate'

type EmailTemplateAccount = Pick<Account, 'name'> & { config: Pick<AccountConfig, 'businessAddress'> }

export interface EmailTemplateContextInput {
  eventType: SchedulingEventType
  client?: BookingClient
  owner?: User
  meetingMode?: MeetingMode
  location?: Location | LocationInput | null
  bookingLink?: string
  unsubscribeLink?: string
  account?: EmailTemplateAccount
  originalStart?: string
}

const FALLBACK_TIMEZONE = 'UTC'

const locationLabel = (mode: MeetingMode, location: Location | LocationInput | null | undefined): string => {
  if (mode === MeetingMode.VIRTUAL) {
    return 'Virtual'
  }
  return location?.address || 'In Person'
}

const isKnownTimezone = (timezone: string | null | undefined): timezone is string => {
  if (!timezone) {
    return false
  }
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: timezone })
    return true
  } catch {
    return false
  }
}

const resolveTimezone = (candidates: (string | null | undefined)[]): string => candidates.find(isKnownTimezone) ?? FALLBACK_TIMEZONE

const formatEmailDate = (moment: Date, timeZone: string): string =>
  new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone }).format(moment)

const formatEmailTime = (moment: Date, timeZone: string): string => {
  const parts = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone }).formatToParts(moment)
  const clock = parts
    .filter((part) => part.type === 'hour' || part.type === 'minute' || (part.type === 'literal' && part.value === ':'))
    .map((part) => part.value)
    .join('')
  const period = parts.find((part) => part.type === 'dayPeriod')?.value ?? ''
  return `${clock} ${period}`
}

const parseInstant = (value: string | undefined): Date | undefined => {
  if (!value) {
    return undefined
  }
  const moment = new Date(value)
  return Number.isNaN(moment.getTime()) ? undefined : moment
}

const originalTimeFields = (originalStart: string | undefined, timezone: string): Record<string, string> => {
  const start = parseInstant(originalStart)
  if (!start) {
    return {}
  }
  return {
    original_date: formatEmailDate(start, timezone),
    original_time: formatEmailTime(start, timezone),
  }
}

export const buildEmailTemplateContext = ({
  eventType,
  client,
  owner,
  meetingMode,
  location,
  bookingLink,
  unsubscribeLink,
  account,
  originalStart,
}: EmailTemplateContextInput): EmailTemplateContext => {
  const mode = meetingMode ?? eventType.meetingMode
  const venueTimezone = mode === MeetingMode.IN_PERSON ? location?.timeZone : undefined
  const zone = resolveTimezone([venueTimezone, owner?.effectiveSettings?.timezone])
  return {
    client_first_name: client?.firstName || client?.lastName || client?.email?.split('@')[0] || 'there',
    client_name: client ? clientDisplayName(client) : '',
    client_email: client?.email ?? '',
    advisor_first_name: owner?.firstName ?? '',
    advisor_last_name: owner?.lastName ?? '',
    event_type_name: eventType.name,
    duration: `${eventType.durationMinutes} minutes`,
    location: locationLabel(mode, location),
    booking_link: bookingLink ?? '',
    unsubscribe_link: unsubscribeLink ?? '',
    firm_name: account?.name ?? '',
    ...(account?.config.businessAddress ? { firm_business_address: account.config.businessAddress } : {}),
    ...originalTimeFields(originalStart, zone),
    timezone: zone,
  }
}
